import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsPoolToken } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { usefromTokens } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';
import { OnchainInteractionsBorrowable } from 'impermax-sdk';


export default function useRepay(approvalState: ButtonState, amount: BigNumber, invalidInput: boolean): [ButtonState, () => Promise<void>] {
  const onchainInteractionsPoolToken = useOnchainInteractionsPoolToken() as OnchainInteractionsBorrowable;
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const val = usefromTokens(amount);
  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const summary = `Repay ${formatFloat(val)} ${symbol}`;
  
  const repayState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalState != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [approvalState, pending, amount]);

  const repay = useCallback(async (): Promise<void> => {
    if (repayState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsPoolToken.repay(amount, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [repayState, onchainInteractionsPoolToken, addTransaction, amount]);

  return [repayState, repay];
}